import React, { useState, useMemo, useCallback, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import {
  CalendarClock, X, AlertTriangle, ArrowRight, ArrowLeft, Loader2, Plus, Trash2,
  History, DollarSign, ShieldAlert, CheckCircle2, Warehouse, ShoppingCart,
} from "lucide-react";
import { PROC_TEAM_COLORS } from "./StagePage/utils";
import { CheckStoresPanel } from "@/components/StoresModals";

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Material sourcing hub / ETA picker ─────────────────────────────────────
   Shown when a Procurement subteam status flips to "Ordered" (and reopenable
   from the row's sourcing pill for Material).
   · Tooling / Gauging: the classic form — ETA (date or explicit "no ETA yet")
     + compulsory order spend (zero allowed, USD/INR).
   · Material: a sourcing hub. First open forks between "Use stores" (reserve
     lots — each reservation writes a stores spend entry server-side) and
     "Order new" (the classic form). Once anything is sourced it shows a
     ledger with add/release actions and two explicit exits that answer the
     status question: All sourced → Complete · More coming → Ordered.
*/
export function EtaPickerModal({ open, taskName, accent, inrRate = 83, onConfirm, onCancel, item = null,
                                 onCompleteFromStores, onKeepOrdered, onSourcingChanged }) {
  const [date, setDate] = useState("");
  const [unknown, setUnknown] = useState(false);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  // Sourcing-hub state (Material only)
  const isMaterial = taskName === "Material";
  const [view, setView] = useState("root");        // root | stores | order
  const [resv, setResv] = useState(null);          // null = loading
  const [storesPreview, setStoresPreview] = useState(null);

  const fetchResv = useCallback(async () => {
    if (!item?.id) return;
    try { const r = await axios.get(`${API}/api/line-items/${item.id}/reservations`); setResv(r.data || []); }
    catch { setResv([]); }
  }, [item?.id]);

  useEffect(() => {
    if (!open) {
      setDate(""); setUnknown(false);
      setAmount(""); setCurrency("USD");
      setView("root"); setResv(null); setStoresPreview(null);
    } else if (isMaterial && item?.id) {
      fetchResv();
      // Light stores preview for the chooser card — total free + top lots.
      axios.get(`${API}/api/stores/check?q=`)
        .then(r => setStoresPreview(r.data || null))
        .catch(() => setStoresPreview(null));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasOrder = !!item && (item.material_status === "Ordered" || item.material_status === "Partially Received");
  const hasSourcing = (resv?.length || 0) > 0 || (isMaterial && hasOrder);

  const releaseReservation = async (r) => {
    try {
      await axios.post(`${API}/api/stores/lots/${r.lot_id}/unreserve?reservation_id=${encodeURIComponent(r.reservation_id)}`);
      toast.success(`Released ${r.quantity} ${r.unit} — spend entry removed`);
      fetchResv(); onSourcingChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to release reservation"); }
  };

  const dateValid = unknown || (date && /^\d{4}-\d{2}-\d{2}$/.test(date));
  const trimmed = (amount ?? "").toString().trim();
  // Spend is COMPULSORY but zero is allowed — only invalid when the field is empty
  // or non-numeric.
  const amt = trimmed === "" ? NaN : parseFloat(trimmed);
  const amountValid = !isNaN(amt) && amt >= 0;
  const valid = dateValid && amountValid;

  const safeRate = Number(inrRate) > 0 ? Number(inrRate) : 83;
  const usdAmount = amountValid
    ? (currency === "INR" ? amt / safeRate : amt)
    : 0;
  const usdRounded = Math.round(usdAmount * 100) / 100;

  const submit = () => {
    if (!valid) return;
    onConfirm({ eta: unknown ? "" : date, spendUsd: usdRounded, spendOriginal: amt, spendCurrency: currency });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="rl-glass-edge bg-[hsl(var(--card))] w-[95vw] md:w-[600px] max-w-[95vw] p-0 overflow-hidden rounded-xl flex flex-col max-h-[88vh]" data-testid="eta-picker-modal">
        <div className="h-[3px] w-full" style={{ background: accent || "#3b82f6" }} />
        <DialogHeader className="px-5 pt-4 pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="w-4 h-4" style={{ color: accent || "#3b82f6" }} />
            <span>
              {!isMaterial ? `${taskName} ordered — ETA & spend?`
                : view === "order" ? "Order new material"
                : view === "stores" ? "Use stores"
                : "Material sourcing"}
            </span>
            {isMaterial && item?.po_number && (
              <span className="ml-auto font-mono-data text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-300 whitespace-nowrap">
                {item.po_number}{item.line_number ? ` · L${item.line_number}` : ""}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-[11px] leading-relaxed">
            {!isMaterial
              ? "Set an estimated arrival date and the spend incurred for this order. Both can be updated later — recording them now keeps the audit log clean."
              : view === "order"
                ? "Set the ETA and the spend for this external order — both can be updated later."
                : view === "stores"
                  ? "Reserve material from stock — its cost lands on this line's spend history automatically."
                  : "Source this line's material — reserve from stores, order new, or both."}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-3 pt-1 space-y-4 min-w-0 flex-1 overflow-y-auto min-h-0">
          {/* ── Material sourcing hub ── */}
          {isMaterial && view === "root" && (
            resv === null ? (
              <div className="py-8 flex items-center justify-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking sourcing…
              </div>
            ) : !hasSourcing ? (
              /* Chooser — nothing sourced yet */
              <div className="space-y-3">
                <button type="button" onClick={() => setView("stores")}
                  className="w-full text-left rounded-lg border border-[hsl(var(--border))] hover:border-orange-500/50 bg-[hsl(var(--muted))]/30 hover:bg-orange-500/5 p-3.5 transition-colors group"
                  data-testid="sourcing-choose-stores">
                  <div className="flex items-center gap-2">
                    <Warehouse className="w-4 h-4 text-orange-500 shrink-0" />
                    <span className="text-sm font-semibold">Use stores</span>
                    <ArrowRight className="w-3.5 h-3.5 ml-auto text-[hsl(var(--muted-foreground))] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  {storesPreview ? (
                    <div className="mt-2 space-y-1">
                      <div className="text-[11px] font-semibold text-emerald-600 font-mono-data">
                        {storesPreview.total_free} free across {(storesPreview.lots || []).length} lots
                      </div>
                      {(storesPreview.lots || []).slice(0, 2).map(l => (
                        <div key={l.id} className="flex items-center justify-between gap-2 text-[10.5px] text-[hsl(var(--muted-foreground))]">
                          <span className="truncate">{l.material}{l.grade ? ` · ${l.grade}` : ""}</span>
                          <span className="font-mono-data font-semibold text-emerald-600 shrink-0">{l.free} {l.unit}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-1.5 text-[11px] text-[hsl(var(--muted-foreground))]">Search stock and reserve — any shape, any lot.</div>
                  )}
                </button>
                <button type="button" onClick={() => setView("order")}
                  className="w-full text-left rounded-lg border border-[hsl(var(--border))] hover:border-blue-500/50 bg-[hsl(var(--muted))]/30 hover:bg-blue-500/5 p-3.5 transition-colors group"
                  data-testid="sourcing-choose-order">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-blue-500 shrink-0" />
                    <span className="text-sm font-semibold">Order new</span>
                    <ArrowRight className="w-3.5 h-3.5 ml-auto text-[hsl(var(--muted-foreground))] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="mt-1.5 text-[11px] text-[hsl(var(--muted-foreground))]">Place an external order — ETA and spend.</div>
                </button>
              </div>
            ) : (
              /* Ledger — what's sourced so far. One hairline-divided glass list,
                 single-line rows: qty · name · meta · cost · action. */
              <div data-testid="sourcing-ledger">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">
                    Sourced · {(resv || []).length + (hasOrder ? 1 : 0)} line{((resv || []).length + (hasOrder ? 1 : 0)) !== 1 ? "s" : ""}
                  </span>
                  {(() => {
                    const t = (resv || []).reduce((s, r) => s + (r.spend_usd || 0), 0);
                    return t > 0 ? (
                      <span className="text-[11px] font-semibold font-mono-data text-emerald-600">
                        ${t.toLocaleString("en-US", { maximumFractionDigits: 0 })} from stores
                      </span>
                    ) : null;
                  })()}
                </div>
                <div className="rounded-[10px] border border-[hsl(var(--border))] overflow-hidden">
                  {(resv || []).map(r => (
                    <div key={r.reservation_id} className="flex items-center gap-2 px-2.5 py-1.5 border-b border-[hsl(var(--border))]/50 last:border-b-0" data-testid={`sourcing-resv-${r.reservation_id}`}>
                      <Warehouse className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      <span className="font-mono-data text-[12px] font-semibold text-emerald-600 shrink-0">{r.quantity} {r.unit}</span>
                      <span className="text-[12px] font-medium truncate shrink-0 max-w-[38%]">{r.material}{r.grade ? ` · ${r.grade}` : ""}</span>
                      <span className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono-data truncate flex-1 min-w-0">
                        {[r.heat_number && `H ${r.heat_number}`, r.dimensions, r.location].filter(Boolean).join(" · ")}
                      </span>
                      {r.spend_usd != null && (
                        <span className="text-[11px] font-semibold font-mono-data text-emerald-700 dark:text-emerald-400 shrink-0">
                          {r.pre_expensed ? "$0" : `$${Number(r.spend_usd).toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                        </span>
                      )}
                      <button type="button" onClick={() => releaseReservation(r)}
                        className="shrink-0 text-[10px] font-semibold text-[hsl(var(--muted-foreground))] hover:text-rose-500 underline decoration-dotted transition-colors"
                        title="Release this hold — removes its spend entry too"
                        data-testid={`sourcing-release-${r.reservation_id}`}>
                        release
                      </button>
                    </div>
                  ))}
                  {hasOrder && (
                    <div className="flex items-center gap-2 px-2.5 py-1.5" data-testid="sourcing-order-line">
                      <ShoppingCart className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      <span className="text-[12px] font-medium shrink-0">External order</span>
                      <span className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono-data truncate flex-1 min-w-0">
                        ETA {item?.material_eta || "N/A"}{item?.material_status === "Partially Received" ? " · partially received" : ""}
                      </span>
                      <button type="button" onClick={() => setView("order")}
                        className="shrink-0 text-[10px] font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] underline decoration-dotted transition-colors"
                        data-testid="sourcing-order-edit">
                        update
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button type="button" onClick={() => setView("stores")}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/8 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15 transition-colors"
                    data-testid="sourcing-add-stores">
                    <Plus className="w-3 h-3" /> From stores
                  </button>
                  <button type="button" onClick={() => setView("order")}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-blue-500/30 bg-blue-500/8 text-blue-700 dark:text-blue-300 hover:bg-blue-500/15 transition-colors"
                    data-testid="sourcing-add-order">
                    <Plus className="w-3 h-3" /> New order
                  </button>
                </div>
              </div>
            )
          )}

          {/* ── Stores sub-view — search + reserve, returns to the ledger ── */}
          {isMaterial && view === "stores" && (
            <div className="space-y-3">
              <CheckStoresPanel defaultQuery="" boundItem={item}
                onReserved={() => { fetchResv(); onSourcingChanged?.(); setView("root"); }} />
            </div>
          )}

          {/* ── Order form — classic ETA + spend (all of Tooling/Gauging; Material's "Order new") ── */}
          {(!isMaterial || view === "order") && (<>
          {/* ETA */}
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">
              ETA <span className="text-rose-400 normal-case font-normal">(required)</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); if (e.target.value) setUnknown(false); }}
              disabled={unknown}
              className="w-full h-10 px-3 rounded-lg bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-sm font-mono-data text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 transition-colors disabled:opacity-50"
              style={{ "--tw-ring-color": accent || "#3b82f6" }}
              data-testid="eta-date-input"
            />
            <button
              type="button"
              onClick={() => { setUnknown((u) => !u); if (!unknown) setDate(""); }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md border text-left transition-colors ${unknown ? "border-amber-500/50 bg-amber-500/10 text-amber-600" : "border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:border-amber-500/30"}`}
              data-testid="eta-unknown-toggle"
            >
              <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${unknown ? "bg-amber-500 border-amber-500" : "border-[hsl(var(--border))]"}`}>
                {unknown && <CheckCircle2 className="w-3 h-3 text-white" />}
              </div>
              <span className="text-xs font-semibold">No ETA known yet — show as "N/A"</span>
            </button>
          </div>

          {/* Spend */}
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] flex items-center justify-between">
              <span>Order Spend <span className="text-rose-400 normal-case font-normal">(required · zero allowed)</span></span>
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-[hsl(var(--muted-foreground))] font-mono-data pointer-events-none">
                  {currency === "USD" ? "$" : "₹"}
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  className="w-full h-10 pl-7 pr-3 rounded-lg bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-sm font-mono-data text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 transition-colors"
                  style={{ "--tw-ring-color": accent || "#3b82f6" }}
                  data-testid="eta-spend-amount-input"
                />
              </div>
              <div
                className="inline-flex rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-0.5 shrink-0"
                role="group"
                aria-label="Currency"
              >
                {["USD", "INR"].map(c => {
                  const sel = currency === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCurrency(c)}
                      className="px-3 h-9 rounded-md text-[11px] font-bold transition-colors"
                      style={sel
                        ? { background: accent || "#3b82f6", color: "#fff" }
                        : { color: "hsl(var(--muted-foreground))" }
                      }
                      data-testid={`eta-spend-currency-${c.toLowerCase()}`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            {currency === "INR" && amountValid && amt > 0 && (
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono-data" data-testid="eta-spend-conversion">
                ≈ ${usdRounded.toLocaleString("en-US", { maximumFractionDigits: 2 })} USD <span className="opacity-60">(rate ₹{safeRate}/$)</span>
              </p>
            )}
          </div>
          </>)}
        </div>

        <div className="px-5 py-3 flex items-center gap-2 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 shrink-0">
          {/* Back — sub-views return to the hub root instead of closing */}
          {isMaterial && view !== "root" && (
            <button onClick={() => { setView("root"); fetchResv(); }}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              data-testid="sourcing-back-btn">
              <ArrowLeft className="w-3 h-3" /> Back
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {isMaterial && view === "root" && hasSourcing ? (
              /* Exit verbs — the user answers the status question, no math */
              <>
                <button onClick={onKeepOrdered}
                  className="px-3 py-1.5 rounded-md text-xs font-bold border border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:bg-blue-500/20 transition-colors"
                  title="Keep the line waiting on more material — status Ordered"
                  data-testid="sourcing-exit-ordered">
                  More coming — {item?.material_status === "Ordered" ? "keep" : "mark"} Ordered
                </button>
                <button onClick={onCompleteFromStores}
                  className="px-3 py-1.5 rounded-md text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
                  title="Everything this line needs is sourced — status Complete"
                  data-testid="sourcing-exit-complete">
                  <CheckCircle2 className="w-3 h-3 inline -mt-px mr-1" />All sourced — Complete
                </button>
              </>
            ) : (!isMaterial || view === "order") ? (
              <>
                {!isMaterial && (
                  <button onClick={onCancel}
                    className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                    data-testid="eta-cancel-btn">Cancel</button>
                )}
                <button
                  onClick={submit}
                  disabled={!valid}
                  className="px-3 py-1.5 rounded-md text-xs font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: accent || "#3b82f6" }}
                  data-testid="eta-confirm-btn"
                >
                  Confirm Order
                </button>
              </>
            ) : (
              <button onClick={onCancel}
                className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                data-testid="eta-cancel-btn">
                {isMaterial && view === "stores" ? "Close" : "Cancel"}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Spend history modal ───────────────────────────────────────────────────
   Replaces the old write-once SpendModal. Shows the audit trail (additions
   + soft-deleted entries with reason) and lets a Procurement user add new
   entries or remove existing ones with a typed reason.
*/
export function SpendHistoryModal({ open, item, onClose, onItemUpdate, canEdit }) {
  const [adding, setAdding] = useState(false);
  const [newSubteam, setNewSubteam] = useState("Material");
  const [newAmount, setNewAmount] = useState("");
  const [newNote, setNewNote] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setAdding(false); setNewSubteam("Material"); setNewAmount(""); setNewNote("");
      setDeletingId(null); setDeleteReason(""); setBusy(false);
    }
  }, [open]);

  const entries = item?.spend_entries || [];
  const total = useMemo(() =>
    entries.filter(e => !e.deleted_at).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0),
    [entries]
  );

  const handleAdd = useCallback(async () => {
    const amt = parseFloat(newAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount > 0"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/line-items/${item.id}/spend-entries`, {
        subteam: newSubteam,
        amount: amt,
        note: newNote.trim(),
      });
      onItemUpdate?.(res.data);
      setAdding(false); setNewAmount(""); setNewNote(""); setNewSubteam("Material");
      toast.success(`+ $${amt.toLocaleString()} added to ${newSubteam}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add spend");
    } finally { setBusy(false); }
  }, [item, newSubteam, newAmount, newNote, onItemUpdate]);

  const handleDelete = useCallback(async (entryId) => {
    const reason = deleteReason.trim();
    if (reason.length < 3) { toast.error("A short reason is required"); return; }
    setBusy(true);
    try {
      const res = await axios.delete(`${API}/api/line-items/${item.id}/spend-entries/${entryId}`, {
        data: { reason },
      });
      onItemUpdate?.(res.data);
      setDeletingId(null); setDeleteReason("");
      toast.success("Spend entry removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove spend");
    } finally { setBusy(false); }
  }, [item, deleteReason, onItemUpdate]);

  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[560px] max-w-[95vw] p-0 overflow-hidden flex flex-col max-h-[88vh]" data-testid="spend-history-modal">
        <div className="h-[3px] w-full bg-gradient-to-r from-emerald-500 to-teal-500" />
        <DialogHeader className="px-5 pt-4 pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <DollarSign className="w-4 h-4 text-emerald-500" />
            <span>Spend history</span>
            <span className="ml-auto font-mono-data text-sm font-bold text-emerald-600">
              ${total.toLocaleString("en-US", { maximumFractionDigits: 0 })} <span className="text-[10px] uppercase tracking-widest font-semibold text-[hsl(var(--muted-foreground))]">total</span>
            </span>
          </DialogTitle>
          <DialogDescription className="text-[11px] leading-relaxed font-mono-data">
            {item.po_number} · L{item.line_number} — {item.item_description}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-2">
          {entries.length === 0 ? (
            <div className="text-center py-10">
              <DollarSign className="w-8 h-8 mx-auto text-[hsl(var(--muted-foreground))] opacity-50" />
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">No spend recorded yet.</p>
            </div>
          ) : (
            <ul className="divide-y divide-[hsl(var(--border))]/60">
              {entries.map(e => {
                const tc = PROC_TEAM_COLORS[e.subteam] || { color: "#64748b", bg: "rgba(100,116,139,0.10)", border: "rgba(100,116,139,0.25)" };
                const deleted = !!e.deleted_at;
                return (
                  <li key={e.id} className={`py-2.5 ${deleted ? "opacity-60" : ""}`} data-testid={`spend-entry-${e.id}`}>
                    <div className="flex items-start gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded shrink-0" style={{ color: tc.color, background: tc.bg, border: `1px solid ${tc.border}` }}>
                        {e.subteam || "Procurement"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`font-mono-data text-sm font-bold ${deleted ? "line-through text-[hsl(var(--muted-foreground))]" : "text-[hsl(var(--foreground))]"}`}>
                          ${parseFloat(e.amount).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                        </p>
                        {e.note && <p className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{e.note}</p>}
                        <p className="text-[10px] text-[hsl(var(--muted-foreground))]">
                          by <span className="font-semibold text-[hsl(var(--foreground))]">{e.added_by}</span>
                          {e.added_by_role && <span className="ml-1 opacity-70">({e.added_by_role})</span>}
                          <span className="opacity-50 mx-1.5">·</span>
                          {new Date(e.added_at).toLocaleDateString()}
                        </p>
                        {deleted && (
                          <p className="text-[10px] text-rose-500 mt-0.5 italic">
                            Removed by {e.deleted_by} ({e.deleted_by_role}) — "{e.delete_reason}"
                          </p>
                        )}
                      </div>
                      {canEdit && !deleted && (
                        deletingId === e.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={deleteReason}
                              onChange={(ev) => setDeleteReason(ev.target.value)}
                              placeholder="Reason…"
                              autoFocus
                              className="h-7 px-2 text-xs rounded border border-rose-500/40 bg-rose-500/5 focus:outline-none focus:ring-1 focus:ring-rose-500 w-[140px]"
                              data-testid={`spend-delete-reason-${e.id}`}
                            />
                            <button onClick={() => handleDelete(e.id)} disabled={busy} className="h-7 px-2 rounded bg-rose-500 text-white text-[10px] font-bold disabled:opacity-50" data-testid={`spend-delete-confirm-${e.id}`}>
                              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Confirm"}
                            </button>
                            <button onClick={() => { setDeletingId(null); setDeleteReason(""); }} className="h-7 w-7 rounded text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] flex items-center justify-center" aria-label="Cancel">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setDeletingId(e.id); setDeleteReason(""); }}
                            className="p-1.5 rounded text-[hsl(var(--muted-foreground))] hover:bg-rose-500/10 hover:text-rose-500 transition-colors"
                            aria-label="Remove this spend entry"
                            data-testid={`spend-delete-btn-${e.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {canEdit && (
          <div className="px-5 py-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 shrink-0">
            {!adding ? (
              <button
                onClick={() => setAdding(true)}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors"
                data-testid="spend-add-open-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add spend entry
              </button>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-1.5">
                  {["Material", "Tooling", "Gauging", "Procurement"].map(st => {
                    const tc = PROC_TEAM_COLORS[st] || { color: "#64748b", bg: "rgba(100,116,139,0.10)", border: "rgba(100,116,139,0.25)" };
                    const sel = newSubteam === st;
                    return (
                      <button
                        key={st}
                        onClick={() => setNewSubteam(st)}
                        className="flex-1 text-[10px] font-bold uppercase tracking-widest py-1.5 rounded border transition-colors"
                        style={sel ? { color: "#fff", background: tc.color, borderColor: tc.color } : { color: tc.color, background: tc.bg, borderColor: tc.border }}
                        data-testid={`spend-add-subteam-${st}`}
                      >
                        {st}
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-1.5">
                  <input
                    type="number"
                    value={newAmount}
                    onChange={(e) => setNewAmount(e.target.value)}
                    placeholder="Amount $"
                    autoFocus
                    className="flex-1 h-9 px-2 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="spend-add-amount"
                  />
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Note (optional)"
                    className="flex-[2] h-9 px-2 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="spend-add-note"
                  />
                </div>
                <div className="flex items-center justify-end gap-1.5">
                  <button onClick={() => setAdding(false)} className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">Cancel</button>
                  <button
                    onClick={handleAdd}
                    disabled={busy || !newAmount}
                    className="px-3 h-8 rounded-md text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                    data-testid="spend-add-save-btn"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Ready-for-Manufacturing alert + lightbox ──────────────────────────────
   Items where stage=Procurement and all 3 subteam statuses === "Complete"
   have nothing left to procure but haven't yet been advanced. Surface
   them so they don't sit forgotten. Styling mirrors the Manufacturing
   stage (the next stop) so the alert feels native to the pipeline.
*/
const MFG_COLOR = "#a855f7";

export function ReadyForMfgAlert({ items, onOpen }) {
  const ready = useMemo(
    () => items.filter(i =>
      i.stage === "Procurement" &&
      !i.is_blocked &&
      i.material_status === "Complete" &&
      i.tooling_status === "Complete" &&
      i.gauging_status === "Complete"
    ),
    [items]
  );
  if (ready.length === 0) return null;
  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 rounded-lg border bg-[hsl(var(--card))] animate-in fade-in slide-in-from-top-1 duration-300"
      style={{ borderColor: MFG_COLOR + "30" }}
      data-testid="ready-for-mfg-alert"
    >
      <span
        className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full"
        style={{ background: MFG_COLOR + "1a", color: MFG_COLOR }}
      >
        <CheckCircle2 className="w-3.5 h-3.5" />
      </span>
      <p className="flex-1 min-w-0 text-xs leading-tight text-[hsl(var(--foreground))]">
        <span className="font-bold font-mono-data" style={{ color: MFG_COLOR }}>{ready.length}</span>
        <span className="text-[hsl(var(--muted-foreground))] ml-1.5">
          {ready.length === 1 ? "item is" : "items are"} fully procured — ready to advance to Manufacturing
        </span>
      </p>
      <button
        onClick={() => onOpen(ready)}
        className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-white transition-colors hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-purple-500"
        style={{ backgroundColor: MFG_COLOR }}
        data-testid="ready-for-mfg-check-btn"
      >
        Review <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}

export function ReadyForMfgLightbox({ open, items, onClose, advanceItem }) {
  const [busyAll, setBusyAll] = useState(false);
  const [perItemBusy, setPerItemBusy] = useState({});

  const handleOne = useCallback(async (id) => {
    setPerItemBusy(b => ({ ...b, [id]: true }));
    await advanceItem(id);
    // The list shrinks naturally as the parent re-filters items; no need to clear busy
  }, [advanceItem]);

  const handleAll = useCallback(async () => {
    setBusyAll(true);
    // Sequential — keeps UI animations satisfying and avoids stampeding the API
    for (const it of items) {
      try { await advanceItem(it.id); } catch (e) { /* per-item toast handles */ }
    }
    setBusyAll(false);
    onClose();
  }, [items, advanceItem, onClose]);

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[680px] max-w-[95vw] p-0 overflow-hidden flex flex-col max-h-[88vh]" data-testid="ready-for-mfg-lightbox">
        <div className="h-[3px] w-full" style={{ background: MFG_COLOR }} />
        <DialogHeader className="px-5 pt-4 pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <CheckCircle2 className="w-4 h-4" style={{ color: MFG_COLOR }} />
            <span>Ready for Manufacturing</span>
            <span
              className="font-mono-data text-[10px] font-bold px-1.5 py-0.5 rounded border"
              style={{ color: MFG_COLOR, borderColor: MFG_COLOR + "40", background: MFG_COLOR + "12" }}
            >
              {items.length}
            </span>
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            All three procurement subteams marked these items Complete. Send each one to Manufacturing — one by one or all at once.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-3 py-1">
          <ul className="divide-y divide-[hsl(var(--border))]/60">
            {items.map(i => (
              <li key={i.id} className="py-2.5 px-2 flex items-center gap-3" data-testid={`ready-row-${i.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-data text-[11px] font-bold text-[hsl(var(--foreground))]">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">PO</span>{i.po_number}
                      {i.line_number && <><span className="opacity-40 mx-1">·</span><span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">LINE</span>{i.line_number}</>}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-[hsl(var(--foreground))] truncate mt-0.5">{i.item_description}</p>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{i.client}</p>
                </div>
                <button
                  onClick={() => handleOne(i.id)}
                  disabled={!!perItemBusy[i.id] || busyAll}
                  className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-white transition-colors hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-purple-500"
                  style={{ backgroundColor: MFG_COLOR }}
                  data-testid={`ready-advance-${i.id}`}
                >
                  {perItemBusy[i.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><span>Advance</span><ArrowRight className="w-3 h-3" /></>}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="px-5 py-3 flex items-center justify-between border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 shrink-0">
          <button onClick={onClose} className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="ready-close-btn">Close</button>
          <button
            onClick={handleAll}
            disabled={busyAll || items.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-white transition-colors hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-purple-500"
            style={{ backgroundColor: MFG_COLOR }}
            data-testid="ready-advance-all-btn"
          >
            {busyAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
            Advance all {items.length}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Helper to format ETA string for inline dropdown trigger ─────────────── */
export function formatEtaShort(iso) {
  if (!iso) return "N/A";
  try {
    const d = new Date(iso + (iso.includes("T") ? "" : "T00:00:00"));
    if (isNaN(d)) return "N/A";
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${String(d.getDate()).padStart(2,"0")}-${months[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
  } catch { return "N/A"; }
}
